import { ConflictMatch } from "./match.js";
import { OnlineAuthorityLifecycle } from "./online-authority-lifecycle.js";
import { validateOnlineRoomCheckpoint } from "./online-room-save.js";
import type { RecoveryCheckpoint } from "./recovery-protocol.js";
import {
  acceptedRoomAck,
  decodeRoomHello,
  decodeRoomOrder,
  requiredRoomToken,
} from "./room-codec.js";
import { reconnectVerifier } from "./room-credentials.js";
import type { RoomChat, RoomHello, RoomOrderAck, RoomRole, RoomStatePacket } from "./room-types.js";
import type { ConflictConfig, ControllerEnvelope } from "./types.js";

export * from "./room-codec.js";
export {
  decodeBoundedRoomPacket,
  decodeRoomPacket,
  encodeRoomPacket,
  ROOM_PACKET_MAX_BYTES,
} from "./room-packet.js";
export type * from "./room-types.js";

interface Member {
  readonly peerId: string;
  readonly hello: RoomHello;
  readonly seatId: string | null;
  ready: boolean;
}

export class OnlineRoomAuthority {
  readonly #config: ConflictConfig;
  readonly #hostSeatId: string;
  readonly #members = new Map<string, Member>();
  readonly #sessions = new Map<
    string,
    {
      readonly verifier: string;
      readonly seatId: string | null;
      readonly role: RoomRole;
    }
  >();
  readonly #humanSeats: readonly string[];
  #match: ConflictMatch | undefined;
  #eventCursors = new Map<string, bigint>();
  #control: OnlineAuthorityLifecycle | undefined;
  #resumeCheckpoint: RecoveryCheckpoint | undefined;

  constructor(config: ConflictConfig, hostPeerId: string, hostHello: RoomHello) {
    const verifiedHost = decodeRoomHello(hostHello);
    if (verifiedHost.role !== "host") throw new TypeError("Room creator must use the host role");
    const hostSeatId = config.seats[0]?.id;
    if (!hostSeatId) throw new TypeError("Online room requires at least one seat");
    this.#config = structuredClone(config);
    this.#hostSeatId = hostSeatId;
    this.#humanSeats = config.seats.slice(0, 8).map((seat) => seat.id);
    this.addMember(hostPeerId, verifiedHost, hostSeatId);
  }

  static resume(
    config: ConflictConfig,
    hostPeerId: string,
    hostHello: RoomHello,
    checkpoint: RecoveryCheckpoint,
  ): OnlineRoomAuthority {
    validateOnlineRoomCheckpoint(config, checkpoint);
    const authority = new OnlineRoomAuthority(config, hostPeerId, hostHello);
    authority.#resumeCheckpoint = structuredClone(checkpoint);
    return authority;
  }

  static recover(
    config: ConflictConfig,
    hostPeerId: string,
    hostHello: RoomHello,
    checkpoint: RecoveryCheckpoint,
    authorityEpoch: number,
    connected: readonly { readonly peerId: string; readonly hello: RoomHello }[],
  ): OnlineRoomAuthority {
    const authority = new OnlineRoomAuthority(config, hostPeerId, { ...hostHello, role: "host" });
    authority.#match = ConflictMatch.restoreAs(checkpoint.save, config.matchId ?? "online-room");
    authority.#match.promoteAuthority(authorityEpoch);
    authority.#members.clear();
    authority.#sessions.clear();
    for (const saved of checkpoint.roster)
      authority.#sessions.set(saved.sessionId, {
        verifier: saved.reconnectVerifier ?? "legacy-checkpoint",
        seatId: saved.seatId,
        role: saved.role,
      });
    const peers = new Map(connected.map((entry) => [entry.hello.sessionId, entry]));
    peers.set(hostHello.sessionId, { peerId: hostPeerId, hello: hostHello });
    for (const saved of checkpoint.roster) {
      const peer = peers.get(saved.sessionId);
      if (!peer) continue;
      const hello = decodeRoomHello(peer.hello);
      authority.addMember(
        peer.peerId,
        saved.sessionId === hostHello.sessionId ? { ...hello, role: "host" } : hello,
        saved.seatId,
      );
    }
    if (!authority.#members.has(hostPeerId))
      throw new TypeError("Elected host is absent from the certified roster");
    authority.#control = OnlineAuthorityLifecycle.recover(
      config,
      checkpoint,
      authority.connectedHumans(),
    );
    return authority;
  }

  join(peerId: string, input: unknown): { readonly seatId: string | null } {
    const hello = decodeRoomHello(input);
    if (hello.role === "host") throw new TypeError("An online room already has a host");
    const prior = this.#sessions.get(hello.sessionId);
    if (prior && prior.verifier !== reconnectVerifier(hello.reconnectToken))
      throw new TypeError("Reconnection token does not match the session");
    if (prior && prior.role !== hello.role)
      throw new TypeError("Reconnection role does not match the session");
    if (this.#match && hello.role === "controller" && !prior)
      throw new TypeError("New controllers cannot join after the room starts");
    const occupied = new Set(
      [...this.#members.values()].flatMap((member) => (member.seatId ? [member.seatId] : [])),
    );
    const seatId =
      prior?.seatId ??
      (hello.role === "controller"
        ? (this.#humanSeats.find((candidate) => !occupied.has(candidate)) ?? null)
        : null);
    if (hello.role === "controller" && !seatId)
      throw new TypeError("No controllable seat is available");
    this.addMember(peerId, hello, seatId);
    if (this.#match && hello.role === "controller" && prior)
      this.control().reclaim(hello.sessionId);
    return Object.freeze({ seatId });
  }

  leave(peerId: string): void {
    const member = this.#members.get(peerId);
    if (member?.seatId && this.#match)
      this.control().disconnect(member.hello.sessionId, this.gameTimeMs);
    this.#members.delete(peerId);
    this.#eventCursors.delete(peerId);
  }

  setReady(peerId: string, ready: boolean): void {
    const member = this.member(peerId);
    if (member.hello.role === "spectator") throw new TypeError("Spectators do not vote readiness");
    if (this.#match) throw new TypeError("Room roster is locked after start");
    member.ready = ready;
  }

  start(peerId: string): void {
    const member = this.member(peerId);
    if (member.hello.role !== "host") throw new TypeError("Only the host can start the room");
    if (this.#match) throw new TypeError("Room already started");
    const controllers = [...this.#members.values()].filter(
      (entry) => entry.hello.role !== "spectator",
    );
    if (controllers.some((entry) => !entry.ready))
      throw new TypeError("Every connected human controller must be ready");
    const resumed = this.#resumeCheckpoint;
    this.#match = resumed
      ? ConflictMatch.restoreAs(resumed.save, this.#config.matchId ?? "online-room")
      : new ConflictMatch(this.#config);
    if (resumed) this.#match.promoteAuthority(resumed.authorityEpoch + 1);
    const humans = controllers.flatMap((entry) =>
      entry.seatId ? [{ seatId: entry.seatId, sessionId: entry.hello.sessionId }] : [],
    );
    this.#control = OnlineAuthorityLifecycle.start(this.#config, humans, this.match(), resumed);
    this.#resumeCheckpoint = undefined;
  }

  requestPause(peerId: string, paused: boolean): void {
    const member = this.member(peerId);
    if (!member.seatId) throw new TypeError("Spectators cannot request pause changes");
    this.control().requestPause(member.hello.sessionId, paused);
  }

  approvePause(peerId: string): void {
    const member = this.member(peerId);
    if (!member.seatId) throw new TypeError("Spectators cannot approve pause changes");
    this.control().approvePause(member.hello.sessionId);
  }

  submit(peerId: string, input: unknown): RoomOrderAck {
    let requestId = "invalid";
    try {
      const member = this.member(peerId);
      const order = decodeRoomOrder(input);
      requestId = order.requestId;
      if (order.roomId !== (this.#config.matchId ?? "online-room"))
        throw new TypeError("Room order names a different room");
      if (!member.seatId) throw new TypeError("Spectators cannot issue game orders");
      const match = this.match();
      const envelope: ControllerEnvelope = {
        matchId: this.#config.matchId ?? "online-room",
        seatId: member.seatId,
        authorityEpoch: order.authorityEpoch,
        controllerGeneration: order.controllerGeneration,
        controllerSequence: order.controllerSequence,
        requestId: order.requestId,
        expectedScopeGeneration: order.expectedScopeGeneration,
        intent: order.intent,
      };
      const result = match.submitAs(member.seatId, envelope);
      if (!result.ok) throw new TypeError(result.reason);
      return acceptedRoomAck(result.receipt);
    } catch (error) {
      return Object.freeze({
        ok: false,
        requestId,
        reason: error instanceof Error ? error.message : "Invalid room order",
      });
    }
  }

  tick(elapsedMs: number): void {
    this.control().tick(this.match(), this.#config, elapsedMs, this.connectedHumans());
  }

  stateFor(peerId: string): RoomStatePacket {
    const member = this.member(peerId);
    const match = this.match();
    const observationSeat = member.seatId ?? this.#hostSeatId;
    const cursor = this.#eventCursors.get(peerId) ?? 0n;
    const events = match.readEventsAs(observationSeat, cursor);
    this.#eventCursors.set(peerId, events.at(-1)?.sequence ?? cursor);
    return Object.freeze({
      authorityEpoch: match.authorityEpoch(),
      seatId: member.seatId,
      controllerGeneration: member.seatId ? match.controllerGeneration(member.seatId) : null,
      nextControllerSequence: member.seatId ? match.nextControllerSequence(member.seatId) : null,
      lifecycle: this.control().state(),
      observation: match.observeAs(observationSeat),
      events,
    });
  }

  recoveryCheckpoint(
    hostMemberId: string,
    voters: readonly string[],
    membershipVersion: number,
  ): RecoveryCheckpoint {
    const match = this.match();
    const observation = match.observeAs(this.#hostSeatId);
    return Object.freeze({
      id: `${match.authorityEpoch()}:${observation.gameTimeMs}:${observation.revision}`,
      authorityEpoch: match.authorityEpoch(),
      membershipVersion,
      gameTimeMs: observation.gameTimeMs,
      revision: observation.revision.toString(),
      hostMemberId,
      voters: Object.freeze([...voters]),
      roster: Object.freeze(
        [...this.#sessions.entries()].map(([sessionId, session]) => ({
          sessionId,
          role: session.role,
          seatId: session.seatId,
          reconnectVerifier: session.verifier,
        })),
      ),
      bots: this.control().bots(),
      lifecycle: this.control().state(),
      save: match.exportRecoverySave(),
    });
  }

  get authorityEpoch(): number {
    return this.match().authorityEpoch();
  }

  get gameTimeMs(): number {
    return this.match().observeAs(this.#hostSeatId).gameTimeMs;
  }

  roster(): readonly {
    readonly peerId: string;
    readonly sessionId: string;
    readonly role: RoomRole;
    readonly seatId: string | null;
    readonly ready: boolean;
  }[] {
    return Object.freeze(
      [...this.#members.values()].map((member) =>
        Object.freeze({
          peerId: member.peerId,
          sessionId: member.hello.sessionId,
          role: member.hello.role,
          seatId: member.seatId,
          ready: member.ready,
        }),
      ),
    );
  }

  chat(peerId: string, id: string, text: string): RoomChat {
    const member = this.member(peerId);
    const trimmed = text.trim();
    if (!trimmed) throw new TypeError("Chat message is empty");
    return Object.freeze({
      id: requiredRoomToken(id, "chat ID"),
      senderSessionId: member.hello.sessionId,
      text: trimmed.slice(0, 256),
    });
  }

  get started(): boolean {
    return this.#match !== undefined;
  }

  private addMember(peerId: string, hello: RoomHello, seatId: string | null): void {
    if (this.#members.has(peerId)) throw new TypeError("Peer already joined");
    if ([...this.#members.values()].some((entry) => entry.hello.sessionId === hello.sessionId))
      throw new TypeError("Session is already connected");
    const member = { peerId, hello, seatId, ready: false };
    this.#members.set(peerId, member);
    this.#sessions.set(hello.sessionId, {
      verifier: reconnectVerifier(hello.reconnectToken),
      seatId,
      role: hello.role,
    });
  }

  private member(peerId: string): Member {
    const member = this.#members.get(peerId);
    if (!member) throw new TypeError("Peer is not admitted to this room");
    return member;
  }

  private match(): ConflictMatch {
    if (!this.#match) throw new TypeError("Room has not started");
    return this.#match;
  }

  private control(): OnlineAuthorityLifecycle {
    if (!this.#control) throw new TypeError("Room lifecycle has not started");
    return this.#control;
  }

  private connectedHumans(): readonly string[] {
    return [...this.#members.values()]
      .filter((member) => member.seatId !== null)
      .map((member) => member.hello.sessionId)
      .sort();
  }
}
